<script lang="ts" setup>
  import { useAuthenticated } from '@othercommerce/foundation';
  import { useBreakpoints } from '@storefront/storefront';
  import { type PropType, computed } from 'vue';
  import type { Breadcrumb, Country } from '@storefront/types';
  import { type Dictionary, type MedicalType, type CustomerType, CustomerType as CustomerTypeEnum } from '@othercommerce/domain';
  import { StoreLayout, AccountLayout, AccountEditFormControlsMedical, AccountEditFormControlsCompany, AccountEditFormControlsPersonal } from '@storefront/components';
  import { Features } from '@shared/features';
  import type { Customer } from '@storefront/resources';

  const props = defineProps({
    countries: { type: Array as PropType<Country[]>, required: true },
    customer: { type: Object as PropType<Customer>, required: true },
    customerType: { type: Object as PropType<Dictionary>, required: true },
    breadcrumbs: { type: Array as PropType<Breadcrumb[]>, required: true },
    registerAgencies: { type: Object as PropType<Dictionary>, required: true },
    medicalPracticeGroups: { type: Object as PropType<Dictionary>, required: true },
    medicalTypes: { type: Object as PropType<MedicalType>, required: true },
    regions: { type: Object as PropType<Dictionary>, required: true },
  });

  const breakpoints = useBreakpoints();
  const session = useAuthenticated();

  const isRPWDLEnabled = Features.medicalContractorsRegistry.isEnabled;

  const readonly = computed(() => {
    return props.customer.b2b;
  });

  function selectedEditComponent(type: CustomerType) {
    if (type === CustomerTypeEnum.MEDICAL && isRPWDLEnabled) return AccountEditFormControlsMedical;
    if (type === CustomerTypeEnum.PERSONAL) return AccountEditFormControlsPersonal;
    if (type === CustomerTypeEnum.COMPANY) return AccountEditFormControlsCompany;
  }

  function selectedFormEditComponentProps(type: CustomerType, data: any) {
    const common = { countries: props.countries, readonly: readonly.value, regions: props.regions };

    switch (type) {
      case CustomerTypeEnum.MEDICAL:
        return {
          data,
          registerAgencies: props.registerAgencies,
          medicalPracticeGroups: props.medicalPracticeGroups,
          medicalTypes: props.medicalTypes,
          countries: props.countries,
          regions: props.regions,
          customerType: props.customer.type,
        };
      case CustomerTypeEnum.PERSONAL:
      case CustomerTypeEnum.COMPANY:
        return common;
      default:
        return {};
    }
  }

  const defaultMedicalType = function (customer: Customer) {
    if (!customer.rpwdlMedicalType) return props.medicalTypes[0]?.value;

    return customer.rpwdlMedicalType;
  };

  defineOptions({
    layout: [StoreLayout, AccountLayout],
    inheritAttrs: false,
  });
</script>
<template>
  <AccountMobileNavigation :href="$route('web.account.show')">
    {{ $t('SHOP.ACCOUNT.HEADER.MY_ACCOUNT') }}
  </AccountMobileNavigation>
  <FormController v-slot="{ data, submit, processing }" :action="$route('web.account.update')" :data="{ ...customer, rpwdlMedicalType: defaultMedicalType(customer) }" class="account-edit" method="PUT">
    <div class="account-edit__content-wrapper">
      <SfTile class="account-edit__content" large>
        <template v-if="breakpoints.lg" #title>
          <h1 class="account-heading account-edit__heading">
            {{ $t('SHOP.ACCOUNT.HEADER.MY_ACCOUNT') }}
          </h1>
        </template>
        <template #default>
          <div class="account-edit__data">
            <div class="account-edit__column">
              <h2 class="account-edit__column-heading">
                {{ $t('SHOP.ACCOUNT.HEADER.CHANGE_DETAILS') }}
              </h2>
              <SfFormRadio :label="$t('SHOP.ACCOUNT.LABEL.CHANGE_ACCOUNT_TYPE')" :options="customerType" :readonly name="type" required />
              <component
                :is="selectedEditComponent(data.type)"
                v-bind="selectedFormEditComponentProps(data.type, data)"
              />
            </div>
            <div class="account-edit__column">
              <h2 class="account-edit__column-heading">
                {{ $t('SHOP.ACCOUNT.HEADER.CHANGE_PASSWORD') }}
              </h2>
              <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.CURRENT_PASSWORD')" name="password" type="password" />
              <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.NEW_PASSWORD')" name="newPassword" type="password" />
              <SfFormInput :label="$t('SHOP.ACCOUNT.LABEL.CONFIRM_PASSWORD')" name="new_password_confirmation" type="password" />
            </div>
          </div>
        </template>
        <template #footer>
          <SfButton :loading="processing" class="account-edit__submit" primary type="submit" @click="submit">
            {{ $t('SHOP.ACTION.SAVE') }}
          </SfButton>
        </template>
      </SfTile>
      <SfTile v-if="session.creditLimit > 0" class="account-edit__content" large>
        <template #title>
          <h2 class="account-heading account-edit__heading">
            {{ $t('SHOP.ACCOUNT.HEADER.CUSTOMER_CREDIT') }}
          </h2>
        </template>
        <template #default>
          <div class="account-edit__data">
            <div class="account-edit__column">
              <h3 class="account-edit__column-label">
                {{ $t('SHOP.ACCOUNT.LABEL.CUSTOMER_CREDIT') }}
              </h3>
              <SfMoney :value="session?.credit" class="account-edit__column-heading" />
            </div>
            <div class="account-edit__column">
              <h3 class="account-edit__column-label">
                {{ $t('SHOP.ACCOUNT.LABEL.CUSTOMER_CREDIT_LIMIT') }}
              </h3>
              <SfMoney :value="session?.creditLimit" class="account-edit__column-heading" />
            </div>
          </div>
        </template>
      </SfTile>
      <SfTile class="account-edit__gdpr" large>
        <template #title>
          <h2 class="account-heading account-edit__heading">
            {{ $t('SHOP.ACCOUNT.HEADER.GDPR_HISTORY') }}
          </h2>
        </template>
        <template #default>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_1') }}</p>
          <ul>
            <li>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_2') }}</li>
            <li>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_3') }}</li>
            <li>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_4') }}</li>
            <li>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_5') }}</li>
            <li>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_6') }}</li>
          </ul>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_7') }}</p>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.GDPR_HISTORY_8') }}</p>
        </template>
        <template #footer>
          <SfButton :href="$route('web.account.history')" primary small target="_blank">
            {{ $t('SHOP.ACCOUNT.ACTION.GDPR_DOWNLOAD') }}
          </SfButton>
        </template>
      </SfTile>
      <SfTile class="account-edit__delete" large>
        <template #title>
          <h2 class="account-heading account-edit__heading">
            {{ $t('SHOP.ACCOUNT.HEADER.ACCOUNT_DESTROY') }}
          </h2>
        </template>
        <template #default>
          <p class="account-edit__delete__alert">{{ $t('SHOP.ACCOUNT.MESSAGE.ACCOUNT_DESTROY_1') }}</p>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.ACCOUNT_DESTROY_2') }}</p>
          <p>{{ $t('SHOP.ACCOUNT.MESSAGE.ACCOUNT_DESTROY_3') }}</p>
        </template>
        <template #footer>
          <SfButton :href="$route('web.account.destroy')" danger method="DELETE" small>
            {{ $t('SHOP.ACCOUNT.ACTION.ACCOUNT_DESTROY') }}
          </SfButton>
        </template>
      </SfTile>
    </div>
  </FormController>

  <RouterView />
</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  .account-edit {
    &__content-wrapper {
      display: flex;
      flex-direction: column;
      gap: 2rem;
    }

    &__column-heading,
    &__newsletter-heading {
      font-size: 1.125rem;
    }

    &__column-label {
      font-size: 0.875rem;
    }

    &__data {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 4rem;
    }

    &__column {
      display: flex;
      flex-direction: column;
      row-gap: 1rem;
    }

    &__newsletter {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__gdpr {
      color: var(--secondary-color);

      ul {
        padding-left: 1.5rem;
        font-size: 0.75rem;
      }

      p {
        font-size: 0.75rem;
      }
    }

    &__delete {
      border: 1px solid var(--danger-border);
      color: var(--secondary-color);

      p {
        font-size: 0.75rem;
      }

      &__alert {
        color: var(--danger-color);
        font-weight: var(--font-weight-semi);
        font-size: 1rem !important;
      }
    }
  }

  @include media.query(xl) {

    .account-edit {
      &__content-wrapper {
        gap: 1rem;
      }

      &__column-heading,
      &__newsletter-heading {
        font-size: 1rem;
      }

      &__data {
        grid-template-columns: 1fr;
        gap: 2rem;
      }

      &__column {
        gap: var(--form-gap-y) var(--form-gap-x);
      }

      &__newsletter {
        margin: 1rem 0;
        gap: 0.5rem;
      }

      &__submit {
        width: 100%;
      }
    }
  }
</style>
